import React, { useState } from 'react';
import ResultOfAptMetrixContainer from '../components/ResultOfAptMetrixContainer';
import { columnOrderForAptA1B1Info, columnOrderForAptMetrix } from '../constant/columnsName';
import SaleModal from '../components/SaleModal';
import BookmarkModal from '../components/BookmarkModal';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { useApiContext } from '../context/ApiContext';
import { useFilterContext } from '../context/aptmetrix/FilterContext';
import { useRegionContext } from '../context/aptmetrix/RegionContext';
import { useAptDataContext } from '../context/aptmetrix/AptDataContext';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import { useSubscriptionAccess } from '../hooks/useSubscriptionAccess';
import { useAptSaleInfo } from '../hooks/useAptSaleInfo';

function AptMetrix() {
  const navigate = useNavigate();
  const { session } = useAppContext();
  const { api } = useApiContext();
  
  const filterState = useFilterContext();
  const { selectedRegionInfo, setSelectedRegionInfo } = useRegionContext();
  const {
    aptMetrixInfo, setAptMetrixInfo,
    isFetchingAptMetrixInfo, setIsFetchingAptMetrixInfo,
    aptMetrixInfoFetchError, setAptMetrixInfoFetchError,
  } = useAptDataContext();
  
  const { loading, hasAccess, subscription, freeTrialInfo } = useSubscriptionAccess(session);
  const {
    aptNo, aptA1B1ListInfo, isFetchingAptA1B1Info, aptA1B1InfoFetchError,
    showSaleModal, setShowSaleModal, modalIsRent, pyeongName, handleSaleClick,
  } = useAptSaleInfo(api);

  useBodyScrollLock(showSaleModal);

  // 찜하기 상태
  const [bookmarkTarget, setBookmarkTarget] = useState(null);

  const handleBookmarkClick = (row) => {
    setBookmarkTarget({
      aptNo: row['apartment_number'],
      name: row['apartment_name'],
      areas: row['exclusive_area'] ? [row['exclusive_area']] : [],
    });
  };

  const handleBookmarkConfirm = async (data) => {
    const { error } = await api.addWatchlistApartment(data);
    if (!error) setBookmarkTarget(null);
  };

  // 조건 저장 핸들러
  const handleSaveCondition = async (name) => {
    const filters = {
      priceFilter: filterState.priceFilter,
      householdFilter: filterState.householdFilter,
      areaFilter: filterState.areaFilter,
      gapFilter: filterState.gapFilter,
      moveInDateFilter: filterState.moveInDateFilter,
      subdistrictFilter: filterState.subdistrictFilter,
      subwayFilter: filterState.subwayFilter,
      gangnamTimeFilter: filterState.gangnamTimeFilter,
      selectedRegion: filterState.filterComponentState?.selectedRegion,
      selectedCities: filterState.filterComponentState?.selectedCities,
    };
    await api.addWatchlistCondition({ name, filters });
  };

  // 지역 선택 핸들러 - 다중 선택 지원
  const handleRegionSelect = (regionData) => {
    // 여러 지역 선택 여부 확인
    const isMultipleRegions = regionData.multipleRegions || false;
    
    // 선택된 도시 정보
    const selectedCities = regionData.selectedCities || [];
    
    // 지역 이름 정보 (다중 지역 지원)
    const regionNames = regionData.regionNames || {};
    
    // 기존 데이터와 새 데이터 병합
    setSelectedRegionInfo({
      ...selectedRegionInfo,
      ...regionData,
      multipleRegions: isMultipleRegions,
      regionNames: regionNames
    });
    
    // 검색 버튼이 클릭된 경우에만 API 호출
    if (regionData.searchClicked || regionData.autoSearch) {
      getAptMetrixInfo(regionData);
    }
  };

  const getAptMetrixInfo = async (regionInfo = selectedRegionInfo) => {
    setIsFetchingAptMetrixInfo(true);
    
    try {
      // 다중 지역 선택 지원 API 호출
      const { data, error } = await api.getAptMetrixInfo(regionInfo);
      
      if (error) {
        console.error('아파트 메트릭스 정보 조회 오류:', error);
        setAptMetrixInfoFetchError(true);
        setAptMetrixInfo(null);
        return;
      }
      
      setAptMetrixInfo(data);
      setAptMetrixInfoFetchError(false);
    } catch (error) {
      console.error('아파트 메트릭스 정보 조회 중 오류 발생:', error);
      setAptMetrixInfoFetchError(true);
      setAptMetrixInfo(null);
    } finally {
      setIsFetchingAptMetrixInfo(false);
    }
  };

  // 로딩 중인 경우
  if (loading) {
    return (
      <div className="flex justify-center items-center p-8 h-[60vh]">
        <p className="text-xl">로딩 중...</p>
      </div>
    );
  }

  // 미로그인 또는 구독이 없는 경우 접근 제한 메시지 표시
  if (!hasAccess) {
    return (
      <div className="flex flex-col justify-center items-center p-4 h-[60vh]">
        <h2 className="text-2xl font-bold mb-4">이 기능을 사용하려면 구독이 필요합니다</h2>
        <p className="text-gray-600 mb-6 text-center">
          AptMetrix 기능은 유료 구독 회원 또는 무료체험 기간 중에만 이용할 수 있습니다.
        </p>
        <div className="flex flex-col sm:flex-row gap-4">
          <button 
            onClick={() => navigate('/price')} 
            className="bg-green-600 text-white px-6 py-2 rounded-md hover:bg-green-700 transition-colors">
            구독 신청하기
          </button>
          <button 
            onClick={() => navigate('/')} 
            className="bg-gray-200 text-gray-800 px-6 py-2 rounded-md hover:bg-gray-300 transition-colors">
            홈으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="m-1 p-1.5 max-w-full overflow-x-hidden">
      <ResultOfAptMetrixContainer
        isFetchingAptMetrixInfo={isFetchingAptMetrixInfo}
        aptMetrixInfoFetchError={aptMetrixInfoFetchError}
        aptMetrixInfo={aptMetrixInfo}
        columnOrderForAptMetrix={columnOrderForAptMetrix}
        onSaleClick={handleSaleClick}
        onBookmarkClick={handleBookmarkClick}
        onSaveCondition={handleSaveCondition}
        onRegionSelect={handleRegionSelect}
      />

      <SaleModal
        showSaleModal={showSaleModal}
        modalIsRent={modalIsRent}
        setShowSaleModal={setShowSaleModal}
        isFetchingAptA1B1Info={isFetchingAptA1B1Info}
        aptA1B1InfoFetchError={aptA1B1InfoFetchError}
        aptA1B1ListInfo={aptA1B1ListInfo}
        columnOrderForAptA1B1Info={columnOrderForAptA1B1Info}
        aptNo={aptNo}
        pyeongName={pyeongName}
      />

      {bookmarkTarget && (
        <BookmarkModal
          isOpen={true}
          onClose={() => setBookmarkTarget(null)}
          onConfirm={handleBookmarkConfirm}
          apartmentName={bookmarkTarget.name}
          aptNo={bookmarkTarget.aptNo}
          availableAreas={bookmarkTarget.areas}
        />
      )}
    </div>
  );
}

export default AptMetrix; 